import { Center, ScrollArea, Stack, useMantineTheme } from '@mantine/core';
import cx from 'clsx';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useAuthStore from '../../store/auth/auth.store';
import { navbarLinks } from '../nav-links/admin';
import type { NavbarChildLink, NavbarLink } from '../nav-links/interface';
import classes from './DefaultSidebar.module.css';

/**
 * Filter navigation links based on user permissions.
 * - Items without `resource` are always visible (e.g., Dashboard)
 * - Parent items are visible if they have permission OR any child has permission
 * - Child items are filtered individually by permission
 */
function filterNavLinks(
  links: NavbarLink[],
  canAccess: (resource: string) => boolean,
): NavbarLink[] {
  return links
    .map(link => {
      // Filter child links first
      const filteredChildren = link.childLinks?.filter(child => {
        if (!child.resource) return true;
        return canAccess(child.resource);
      });

      return {
        ...link,
        childLinks: filteredChildren,
      };
    })
    .filter(link => {
      // No resource = always visible (e.g., Dashboard)
      if (!link.resource) return true;

      // If parent has permission, show it
      if (canAccess(link.resource)) return true;

      // If any child is visible, show parent
      if (link.childLinks && link.childLinks.length > 0) return true;

      return false;
    });
}

export function DefaultSidebar() {
  const theme = useMantineTheme();
  const { hasAnyPermission, userPermissions } = useAuthStore();

  const [active, setActive] = useState('Dashboard');
  const [childActive, setChildActive] = useState('');

  // Filter nav links based on user permissions
  // Include userPermissions in deps to recalculate when permissions change
  const filteredNavLinks = useMemo(
    () => filterNavLinks(navbarLinks, hasAnyPermission),
    [hasAnyPermission, userPermissions],
  );

  const handleParentClick = (
    navbarLink: NavbarLink,
    filteredChildren: NavbarChildLink[] | undefined,
  ) => {
    setActive(navbarLink.label);
    setChildActive(
      filteredChildren && filteredChildren.length > 0
        ? filteredChildren[0].label
        : '',
    );
  };

  return (
    <div className={classes.navbar}>
      <ScrollArea h='100%' className={classes.pad} type='auto'>
        <div className={classes.links}>
          {filteredNavLinks.map(navbarLink => (
            <Stack
              key={navbarLink.label}
              gap={0}
              style={{ borderRadius: theme.radius.sm, overflow: 'hidden' }}
            >
              <Link
                to={navbarLink.link}
                className={cx(classes.link, {
                  [classes.linkActive]: navbarLink.label === active,
                })}
                onClick={() =>
                  handleParentClick(navbarLink, navbarLink.childLinks)
                }
              >
                <navbarLink.icon className={classes.linkIcon} size={20} />
                <span>{navbarLink.label}</span>
              </Link>
              {navbarLink.label === active &&
                navbarLink.childLinks?.map(childLink => (
                  <Link
                    key={childLink.label}
                    to={childLink.link}
                    className={cx(classes.link, {
                      [classes.childLinkActive]:
                        childLink.label === childActive,
                    })}
                    onClick={() => setChildActive(childLink.label)}
                  >
                    <Center
                      style={{ width: 24, marginRight: theme.spacing.sm }}
                    >
                      <div className={classes.childLinkDot} />
                    </Center>
                    <span>{childLink.label}</span>
                  </Link>
                ))}
            </Stack>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}
